import { useCallback, useEffect, useState } from "react";

import { ApiError, staff, type StaffShift, type StaffState } from "./api";

/**
 * Équipe : rôles et planning des bénévoles.
 *
 * Phase 2 (STAFF.md §5) : importer le planning et gérer les rôles. Le planning
 * vient de l'outil de l'organisateur ; Festipal l'affichera et le rappellera, il
 * ne l'établit pas. L'écran « Mon planning » de l'application se conçoit avec le
 * festival pilote.
 */

type Preview = {
  issues: { line: number; message: string }[];
  shifts?: StaffShift[];
  total?: number;
  people: number;
  withAccount: number;
};

/** "1 créneau", "3 créneaux": real plurals rather than "créneau(x)". */
function count(n: number, one: string, many: string): string {
  return `${n} ${n > 1 ? many : one}`;
}

/** Messages of the server, in words. */
function roleError(cause: unknown): string {
  const code = cause instanceof ApiError ? cause.code : "";
  if (code === "no_account") {
    return "Aucun compte n'utilise cette adresse. La personne doit d'abord créer le sien avec une invitation.";
  }
  if (code === "cannot_demote_self") {
    return "Vous ne pouvez pas vous retirer votre propre rôle d'organisateur.";
  }
  return "Modification impossible pour le moment.";
}

export function StaffEditor({ slug }: { slug: string }) {
  const [state, setState] = useState<StaffState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [email, setEmail] = useState("");
  const [content, setContent] = useState("");
  const [defaultDay, setDefaultDay] = useState("");
  const [preview, setPreview] = useState<Preview | null>(null);

  const reload = useCallback(async () => {
    setState(await staff.get(slug));
  }, [slug]);

  useEffect(() => {
    void reload().catch(() => setError("Lecture impossible."));
  }, [reload]);

  if (!state)
    return error ? (
      <p className="error">{error}</p>
    ) : (
      <p className="muted">Chargement…</p>
    );

  const at = (iso: string, withDay = false) =>
    new Date(iso).toLocaleString("fr-FR", {
      timeZone: state.timezone,
      ...(withDay ? { weekday: "short", day: "numeric", month: "short" } : {}),
      hour: "2-digit",
      minute: "2-digit",
    });

  /** Runs an action, reloads, and says whether it went through. */
  const run = async (action: () => Promise<unknown>, done: string | null) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await action();
      if (done) setMessage(done);
      await reload();
      return true;
    } catch (cause) {
      setError(roleError(cause));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const setRole = (target: string, change: { staff?: boolean; organizer?: boolean }) =>
    run(() => staff.setRole(slug, { email: target, ...change }), null);

  const analyse = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      setPreview(
        await staff.importPlanning(slug, {
          content,
          ...(defaultDay ? { defaultDay } : {}),
        }),
      );
    } catch {
      setError("Fichier illisible.");
    } finally {
      setBusy(false);
    }
  };

  const confirm = () =>
    run(async () => {
      const result = await staff.importPlanning(slug, {
        content,
        ...(defaultDay ? { defaultDay } : {}),
        dryRun: false,
      });
      setPreview(null);
      setContent("");
      setMessage(
        `Planning remplacé : ${count(result.created ?? 0, "créneau", "créneaux")} pour ${count(result.people, "personne", "personnes")}.`,
      );
    }, null);

  const waiting = state.people.filter((p) => p.hasAccount && !p.isStaff).length;

  return (
    <>
      {error ? <p className="error">{error}</p> : null}
      {message ? <p className="badge">{message}</p> : null}

      <section className="card">
        <h2>Rôles</h2>
        <p className="muted">
          Un organisateur modifie le festival. Le staff aura dans l'application son
          planning, les lieux réservés et les consignes : cet écran arrive avec le
          festival pilote, et vous préparez dès maintenant les rôles et le planning.
          L'accès staff s'arrête de lui-même le lendemain du festival (
          {new Date(state.staffUntilDefault).toLocaleDateString("fr-FR", {
            timeZone: state.timezone,
          })}
          ).
        </p>

        {state.members.length > 0 ? (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Adresse</th>
                  <th>Rôles</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {state.members.map((member) => (
                  <tr key={member.userId}>
                    <td>{member.displayName}</td>
                    <td>{member.email ?? "—"}</td>
                    <td>
                      {[
                        member.isOrganizer ? "organisateur" : null,
                        member.isStaff ? "staff" : null,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </td>
                    <td>
                      {member.email && member.isStaff ? (
                        <button
                          className="danger quiet"
                          disabled={busy}
                          onClick={() =>
                            void setRole(member.email as string, { staff: false })
                          }
                        >
                          Retirer l'accès staff
                        </button>
                      ) : null}
                      {member.email && member.isOrganizer ? (
                        <button
                          className="danger quiet"
                          disabled={busy}
                          onClick={() =>
                            void setRole(member.email as string, { organizer: false })
                          }
                        >
                          Retirer le rôle d'organisateur
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted">Personne n'a encore de rôle sur ce festival.</p>
        )}

        <div className="row" style={{ alignItems: "flex-end" }}>
          <label style={{ flex: 1, minWidth: 240 }}>
            Adresse électronique du compte
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="prenom@exemple.fr"
            />
          </label>
          <button
            disabled={busy || !email.includes("@")}
            onClick={() =>
              void setRole(email.trim(), { staff: true }).then(
                (ok) => ok && setEmail(""),
              )
            }
          >
            Donner l'accès staff
          </button>
          <button
            disabled={busy || !email.includes("@")}
            onClick={() =>
              void setRole(email.trim(), { organizer: true }).then(
                (ok) => ok && setEmail(""),
              )
            }
          >
            Nommer organisateur
          </button>
        </div>
      </section>

      <section className="card">
        <h2>Planning des bénévoles</h2>
        <p className="muted">
          Exportez le planning de votre outil en CSV, puis déposez-le ici. Une ligne par
          créneau : adresse électronique, poste, jour, début et fin au minimum ; nom,
          équipe, lieu, référent et consignes si vous les avez. Un nouvel import
          remplace le planning entier.
        </p>

        <div className="row">
          <input
            type="file"
            accept=".csv,text/csv,text/plain"
            onChange={async (event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              setContent(await file.text());
              setPreview(null);
            }}
          />
          <label style={{ minWidth: 200 }}>
            Journée par défaut
            <input
              type="date"
              value={defaultDay}
              onChange={(e) => setDefaultDay(e.target.value)}
            />
          </label>
        </div>

        <label>
          Contenu
          <textarea
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              setPreview(null);
            }}
            placeholder="Courriel;Nom;Équipe;Poste;Lieu;Jour;Début;Fin"
          />
        </label>

        <div className="row">
          <button onClick={() => void analyse()} disabled={busy || !content.trim()}>
            Analyser
          </button>
          <button
            className="primary"
            onClick={() => void confirm()}
            disabled={busy || !preview || (preview.total ?? 0) === 0}
          >
            Remplacer le planning{" "}
            {preview?.total ? `(${count(preview.total, "créneau", "créneaux")})` : ""}
          </button>
        </div>

        {preview ? (
          <>
            <p className="muted">
              {count(preview.total ?? 0, "créneau", "créneaux")} pour{" "}
              {count(preview.people, "personne", "personnes")}, dont{" "}
              {preview.withAccount} avec un compte.
              {state.shifts.length > 0
                ? ` Le planning actuel (${count(state.shifts.length, "créneau", "créneaux")}) sera remplacé.`
                : ""}
            </p>
            {preview.issues.length > 0 ? (
              <div className="error">
                <strong>
                  {count(preview.issues.length, "ligne écartée", "lignes écartées")}
                </strong>
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {preview.issues.slice(0, 10).map((issue) => (
                    <li key={issue.line}>
                      Ligne {issue.line} : {issue.message}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <ShiftTable shifts={preview.shifts ?? []} at={at} />
          </>
        ) : null}
      </section>

      {state.people.length > 0 ? (
        <section className="card">
          <h2>Personnes du planning</h2>
          {waiting > 0 ? (
            <div className="row">
              <button
                className="primary"
                disabled={busy}
                onClick={() =>
                  void run(
                    () => staff.grantPlanned(slug),
                    `Accès staff donné à ${count(waiting, "personne", "personnes")}.`,
                  )
                }
              >
                {waiting > 1
                  ? `Donner l'accès staff aux ${waiting} personnes inscrites`
                  : "Donner l'accès staff à la personne inscrite"}
              </button>
            </div>
          ) : null}
          <p className="muted">
            Une personne sans compte apparaît ici, mais ne peut pas encore recevoir
            d'accès : il lui faut d'abord une invitation.
          </p>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Adresse</th>
                  <th>Créneaux</th>
                  <th>Compte</th>
                  <th>Accès staff</th>
                </tr>
              </thead>
              <tbody>
                {state.people.map((person) => (
                  <tr key={person.email}>
                    <td>{person.name ?? "—"}</td>
                    <td>{person.email}</td>
                    <td>{person.shifts}</td>
                    <td>{person.hasAccount ? "oui" : "non"}</td>
                    <td>{person.isStaff ? "oui" : "non"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {state.shifts.length > 0 ? (
        <section className="card">
          <h2>Planning actuel</h2>
          <ShiftTable shifts={state.shifts} at={at} />
        </section>
      ) : null}
    </>
  );
}

function ShiftTable({
  shifts,
  at,
}: {
  shifts: StaffShift[];
  at: (iso: string, withDay?: boolean) => string;
}) {
  if (shifts.length === 0) return null;
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th>Créneau</th>
            <th>Personne</th>
            <th>Équipe</th>
            <th>Poste</th>
            <th>Lieu</th>
            <th>Référent</th>
          </tr>
        </thead>
        <tbody>
          {shifts.map((shift, index) => (
            <tr key={shift.id ?? index}>
              <td className="nowrap">
                {at(shift.startsAt, true)} → {at(shift.endsAt)}
              </td>
              <td>{shift.name ?? shift.email}</td>
              <td>{shift.team ?? "—"}</td>
              <td>{shift.post}</td>
              <td>{shift.place ?? "—"}</td>
              <td>{shift.referent ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
